import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TourProvider as ReactTourProvider } from '@reactour/tour';

const TourContext = createContext();

// Comprehensive tour step configurations
const TOUR_STEPS = {
  // Welcome tour for first-time users - comprehensive walkthrough
  welcome: [
    {
      selector: '[data-tour="sidebar-logo"]',
      content: {
        title: '👋 Welcome to Zentry!',
        body: "We're excited to have you on board! This quick tour will show you how to manage your compliance efficiently. Let's get started!"
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-dashboard"]',
      content: {
        title: '📊 Your Dashboard',
        body: 'This is your compliance command center. See your overall compliance score, control status, and recent activities at a glance.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-frameworks"]',
      content: {
        title: '📁 Compliance Frameworks',
        body: 'Choose from 15+ pre-built frameworks like SOC 2, ISO 27001, HIPAA, and PCI-DSS. Select the ones relevant to your organization.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-controls"]',
      content: {
        title: '✅ Controls Management',
        body: 'This is where "Map Once, Comply Many" happens! Mark a control compliant once, and it automatically updates across all mapped frameworks.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-evidence-lab"]',
      content: {
        title: '📎 Evidence Lab',
        body: 'Upload and organize your compliance evidence here. Link evidence to controls and track validity periods.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-risk-register"]',
      content: {
        title: '⚠️ Risk Register',
        body: 'Track organizational risks with AI-powered assessments. Define impact, likelihood, and treatment plans.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-vulnerabilities"]',
      content: {
        title: '🛡️ Vulnerability Management',
        body: 'Track security vulnerabilities with SLA monitoring. Never miss a remediation deadline.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-soc-2-readiness"]',
      content: {
        title: '🎯 SOC 2 Readiness',
        body: 'Preparing for SOC 2? This dedicated page tracks your readiness across all Trust Services Criteria.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="nav-policies"]',
      content: {
        title: '📄 Policy Management',
        body: 'Create, version, and track policy acknowledgments. Essential for governance and audits.'
      },
      position: 'right',
    },
    {
      selector: '[data-tour="page-content"]',
      content: {
        title: '🚀 Ready to Go!',
        body: "You're all set! Start by selecting your frameworks, then work through controls and upload evidence. Click the Help button anytime for more tours."
      },
      position: 'center',
    },
  ],
  
  // Dashboard specific tour
  dashboard: [
    {
      selector: '[data-tour="compliance-score"]',
      content: {
        title: '📈 Compliance Score',
        body: 'Your overall compliance percentage across all enabled frameworks. Aim for 80%+ before audits!'
      },
    },
    {
      selector: '[data-tour="stats-grid"]',
      content: {
        title: '📊 Key Metrics',
        body: 'Quick view of your controls, evidence, risks, and policies. Click any card to dive deeper.'
      },
    },
    {
      selector: '[data-tour="framework-compliance"]',
      content: {
        title: '🎯 Framework Progress',
        body: 'See how you\'re progressing in each framework. Green means compliant, yellow is pending, red needs attention.'
      },
    },
    {
      selector: '[data-tour="download-report"]',
      content: {
        title: '📥 Export Reports',
        body: 'Download compliance reports for executives or auditors. Available in PDF format.'
      },
    },
  ],

  // Controls page tour
  controls: [
    {
      selector: '[data-tour="controls-page"]',
      content: {
        title: '✅ Controls Overview',
        body: 'This is your control inventory. Each control maps to specific requirements across frameworks.'
      },
    },
    {
      selector: '[data-tour="controls-filter"]',
      content: {
        title: '🔍 Filter Controls',
        body: 'Filter by framework, status, or search by name. Find what you need quickly.'
      },
    },
    {
      selector: '[data-tour="control-status"]',
      content: {
        title: '🚦 Control Status',
        body: 'Click on any control to update its status: Compliant (green), Pending (yellow), or Gap (red).'
      },
    },
  ],

  // Evidence Lab tour
  evidence: [
    {
      selector: '[data-tour="evidence-page"]',
      content: {
        title: '📎 Evidence Library',
        body: 'All your compliance evidence in one place. Upload documents, screenshots, and reports.'
      },
    },
    {
      selector: '[data-tour="upload-evidence"]',
      content: {
        title: '⬆️ Upload Evidence',
        body: 'Click here to upload new evidence. Supported formats: PDF, images, docs, and more.'
      },
    },
    {
      selector: '[data-tour="evidence-list"]',
      content: {
        title: '📋 Evidence Items',
        body: 'Each item shows title, linked controls, and validity. Click to view details or edit.'
      },
    },
  ],

  // SOC 2 Readiness tour
  soc2: [
    {
      selector: '[data-tour="soc2-page"]',
      content: {
        title: '🎯 SOC 2 Readiness',
        body: 'Everything you need to prepare for your SOC 2 Type II audit in one place.'
      },
    },
    {
      selector: '[data-tour="soc2-score"]',
      content: {
        title: '📊 Readiness Score',
        body: 'Your overall SOC 2 compliance score. Each Trust Services Criteria is scored separately.'
      },
    },
    {
      selector: '[data-tour="soc2-controls"]',
      content: {
        title: '✅ SOC 2 Controls',
        body: '19 controls across Security, Availability, Processing Integrity, Confidentiality, and Privacy.'
      },
    },
    {
      selector: '[data-tour="audit-checklist"]',
      content: {
        title: '📝 Audit Checklist',
        body: 'Work through this checklist before your audit. Track progress on each item.'
      },
    },
  ],

  // Vulnerabilities tour
  vulnerabilities: [
    {
      selector: '[data-tour="vuln-page"]',
      content: {
        title: '🛡️ Vulnerability Dashboard',
        body: 'Track all security vulnerabilities from scanners or manual entry.'
      },
    },
    {
      selector: '[data-tour="vuln-stats"]',
      content: {
        title: '📊 Severity Overview',
        body: 'See vulnerabilities by severity: Critical (fix immediately), High, Medium, and Low.'
      },
    },
    {
      selector: '[data-tour="vuln-sla"]',
      content: {
        title: '⏰ SLA Tracking',
        body: 'Each severity has an SLA. Track which vulnerabilities are on time or overdue.'
      },
    },
    {
      selector: '[data-tour="add-vuln"]',
      content: {
        title: '➕ Add Vulnerability',
        body: 'Manually add vulnerabilities or connect scanners for automatic import.'
      },
    },
  ],

  // Risks tour
  risks: [
    {
      selector: '[data-tour="risks-page"]',
      content: {
        title: '⚠️ Risk Register',
        body: 'Document and track organizational risks. Essential for any compliance program.'
      },
    },
    {
      selector: '[data-tour="add-risk"]',
      content: {
        title: '➕ Add Risk',
        body: 'Click to add a new risk. Define impact, likelihood, and treatment plans.'
      },
    },
    {
      selector: '[data-tour="ai-assess"]',
      content: {
        title: '🤖 AI Assessment',
        body: 'Let AI analyze your risk and suggest appropriate scores and mitigations.'
      },
    },
  ],

  // Threat Intelligence tour
  threatIntel: [
    {
      selector: '[data-tour="threat-page"]',
      content: {
        title: '🌐 Threat Intelligence',
        body: 'Stay informed about the latest security threats and vulnerabilities.'
      },
    },
    {
      selector: '[data-tour="cve-search"]',
      content: {
        title: '🔍 CVE Search',
        body: 'Search the National Vulnerability Database for specific CVEs.'
      },
    },
    {
      selector: '[data-tour="mitre-attack"]',
      content: {
        title: '⚔️ MITRE ATT&CK',
        body: 'Browse tactics and techniques used by threat actors. Map to your defenses.'
      },
    },
  ],
};

// Custom styles for tour popover
const tourStyles = {
  popover: (base) => ({
    ...base,
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '0',
    boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
    maxWidth: '380px',
    border: '1px solid #e2e8f0',
  }),
  maskArea: (base) => ({
    ...base,
    rx: 6,
  }),
  badge: (base) => ({
    ...base,
    backgroundColor: '#0f172a',
    color: '#ffffff',
    fontWeight: '600',
    fontSize: '12px',
    padding: '4px 10px',
    borderRadius: '4px',
  }),
  controls: (base) => ({
    ...base,
    marginTop: '0',
    padding: '16px 20px',
    borderTop: '1px solid #f1f5f9',
    backgroundColor: '#fafbfc',
    borderRadius: '0 0 8px 8px',
  }),
  close: (base) => ({
    ...base,
    color: '#64748b',
    width: '28px',
    height: '28px',
    right: '12px',
    top: '12px',
  }),
  arrow: (base) => ({
    ...base,
    color: '#ffffff',
  }),
};

// Custom content component for richer tour steps
function TourContent({ content }) {
  if (typeof content === 'string') {
    return <p className="text-slate-600 text-sm leading-relaxed">{content}</p>;
  }
  
  return (
    <div className="p-5">
      <h3 className="text-lg font-bold text-slate-900 mb-2">{content.title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{content.body}</p>
    </div>
  );
}

function TourWrapper({ children }) {
  const [completedTours, setCompletedTours] = useState(() => {
    try {
      const saved = localStorage.getItem('zentry_completed_tours');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [currentTourId, setCurrentTourId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  // Check if first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('zentry_has_visited');
    if (!hasVisited) {
      setIsFirstVisit(true);
    }
  }, []);

  // Save completed tours
  useEffect(() => {
    localStorage.setItem('zentry_completed_tours', JSON.stringify(completedTours));
  }, [completedTours]);

  // Format steps for ReactTourProvider
  const formattedSteps = steps.map(step => ({
    ...step,
    content: <TourContent content={step.content} />,
  }));

  const startTour = useCallback((tourId) => {
    const tourSteps = TOUR_STEPS[tourId];
    if (tourSteps) {
      setSteps(tourSteps);
      setCurrentTourId(tourId);
    }
  }, []);

  const autoStartWelcomeTour = useCallback(() => {
    if (isFirstVisit && !completedTours.includes('welcome')) {
      setTimeout(() => {
        startTour('welcome');
        localStorage.setItem('zentry_has_visited', 'true');
        setIsFirstVisit(false);
      }, 2000);
    }
  }, [isFirstVisit, completedTours, startTour]);

  const dismissFirstVisit = useCallback(() => {
    localStorage.setItem('zentry_has_visited', 'true');
    setIsFirstVisit(false);
  }, []);

  const autoStartTour = useCallback((tourId) => {
    if (!completedTours.includes(tourId)) {
      setTimeout(() => startTour(tourId), 1500);
    }
  }, [completedTours, startTour]);

  const resetTours = useCallback(() => {
    setCompletedTours([]);
    localStorage.removeItem('zentry_completed_tours');
    localStorage.removeItem('zentry_has_visited');
    setIsFirstVisit(true);
  }, []);

  const handleTourClose = useCallback(() => {
    if (currentTourId && !completedTours.includes(currentTourId)) {
      setCompletedTours(prev => [...prev, currentTourId]);
    }
    setCurrentTourId(null);
    setSteps([]);
  }, [currentTourId, completedTours]);

  const value = {
    startTour,
    autoStartTour,
    autoStartWelcomeTour,
    dismissFirstVisit,
    resetTours,
    completedTours,
    isNewUser: completedTours.length === 0,
    isFirstVisit,
    currentTourId,
    availableTours: Object.keys(TOUR_STEPS),
  };

  return (
    <TourContext.Provider value={value}>
      <ReactTourProvider
        steps={formattedSteps}
        isOpen={steps.length > 0}
        onClickClose={handleTourClose}
        onClickMask={() => {}}
        styles={tourStyles}
        showBadge={true}
        showCloseButton={true}
        showNavigation={true}
        showDots={true}
        disableInteraction={false}
        padding={{ mask: 8, popover: [8, 12] }}
        badgeContent={({ totalSteps, currentStep }) => `${currentStep + 1} of ${totalSteps}`}
        prevButton={({ currentStep, setCurrentStep }) => (
          <button
            onClick={() => setCurrentStep(currentStep - 1)}
            disabled={currentStep === 0}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
        )}
        nextButton={({ currentStep, setCurrentStep, stepsLength }) => {
          const isLast = currentStep === stepsLength - 1;
          return (
            <button
              onClick={() => {
                if (isLast) {
                  handleTourClose();
                } else {
                  setCurrentStep(currentStep + 1);
                }
              }}
              className="px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded hover:bg-slate-800 transition-colors"
            >
              {isLast ? 'Get Started!' : 'Next'}
            </button>
          );
        }}
      >
        {children}
      </ReactTourProvider>
    </TourContext.Provider>
  );
}

export function TourProvider({ children }) {
  return <TourWrapper>{children}</TourWrapper>;
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    return {
      startTour: () => {},
      autoStartTour: () => {},
      autoStartWelcomeTour: () => {},
      resetTours: () => {},
      completedTours: [],
      isNewUser: false,
      isFirstVisit: false,
      currentTourId: null,
      availableTours: [],
    };
  }
  return context;
}

export default TourProvider;
